import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminListFilterDropdown,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMetadataText,
  AdminMetric,
  AdminMetricDelta,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  adminFilterDateInputClasses,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_SECTION_OFFSET_CLASS,
  ADMIN_METRIC_PANEL_CLASS,
  ADMIN_CONTENT_PANEL_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import useClickOutside from '../../../Hooks/useClickOutside';
import * as echarts from 'echarts';

const formatMetric = (value) => {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString();
};

const renderDelta = (change) => {
  if (change === null || change === undefined) return null;
  const isIncrease = change >= 0;
  const colorClass = isIncrease ? 'text-freshgreen' : 'text-alertred';
  const arrow = isIncrease ? '↑' : '↓';

  return (
    <div className="flex items-center gap-1 mt-1">
      <span className={`${colorClass} text-[12px] leading-none`}>{arrow}</span>
      <span className={` ${colorClass}`}>{isIncrease ? '+' : ''}{Math.abs(change).toFixed(1)}%</span>
    </div>
  );
};

const TIMEFRAME_OPTIONS = ['Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'Custom Range'];

// ECharts Component Wrapper
const EChartComponent = ({ option, style = { width: '100%', height: '300px' } }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !option) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.dispose();
    }

    const chart = echarts.init(chartRef.current);
    chartInstanceRef.current = chart;
    chart.setOption(option);

    const handleResize = () => {
      chart.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, [option]);

  return <div ref={chartRef} style={style} />;
};

// Generate mock data for compliance reports
const generateMockData = (timeframe) => {
  const now = new Date();
  const days = timeframe === 'Last 7 Days' ? 7 : timeframe === 'Last 30 Days' ? 30 : 90;
  
  // Generate daily submission data
  const dailySubmissions = [];
  const dailyVerified = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    dailySubmissions.push({
      date: date.toISOString().split('T')[0],
      count: Math.floor(Math.random() * 30) + 15,
    });
    dailyVerified.push({
      date: date.toISOString().split('T')[0],
      count: Math.floor(Math.random() * 25) + 10,
    });
  }

  // Documents by Category
  const documentsByCategory = [
    { category: 'Licence', count: 642 },
    { category: 'Ticket', count: 381 },
    { category: 'Badge', count: 261 },
  ];

  // Verification Outcomes
  const verificationOutcomes = {
    'Approved': 856,
    'Rejected': 187,
    'Resubmission Required': 142,
    'Expired': 99,
  };

  // Expiry Risk Window
  const expiryRiskWindow = {
    'Expiring in 7 days': 45,
    'Expiring in 14 days': 67,
    'Expiring in 30 days': 74,
  };

  // Verification Method
  const verificationMethod = {
    'Manual': 542,
    'Auto': 612,
    'Mixed': 130,
  };

  return {
    dailySubmissions,
    dailyVerified,
    documentsByCategory,
    verificationOutcomes,
    expiryRiskWindow,
    verificationMethod,
  };
};

const ComplianceReports = () => {
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useState('Last 30 Days');
  const [timeframeOpen, setTimeframeOpen] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [submissionToggle, setSubmissionToggle] = useState('Submitted'); // 'Submitted' or 'Verified'

  const timeframeRef = useRef(null);

  useClickOutside(timeframeRef, () => setTimeframeOpen(false));

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const data = generateMockData(timeframe);
        setReportData(data);
      } catch (error) {
        console.error('Error fetching compliance reports:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeframe]);

  // Calculate Summary Indicators
  const summaryIndicators = useMemo(() => {
    if (!reportData) return null;

    return {
      documentsSubmitted: 1284,
      pendingVerification: 143,
      avgVerificationTime: '2.6 days',
      expiringIn30Days: 186,
      nonCompliantBusinesses: 41,
    };
  }, [reportData]);

  // Documents Submitted Over Time Chart
  const documentsTrendOption = useMemo(() => {
    if (!reportData) return null;

    const data = submissionToggle === 'Submitted' ? reportData.dailySubmissions : reportData.dailyVerified;

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
      },
      grid: {
        top: 20,
        right: 20,
        bottom: 40,
        left: 50,
      },
      xAxis: {
        type: 'category',
        data: data.map(d => d.date.slice(5)),
      },
      yAxis: {
        type: 'value',
      },
      series: [
        {
          name: submissionToggle,
          type: 'line',
          data: data.map(d => d.count),
          smooth: true,
          lineStyle: { color: '#1E40AF', width: 2 },
          areaStyle: { color: 'rgba(30, 64, 175, 0.1)' },
        },
      ],
    };
  }, [reportData, submissionToggle]);

  // Documents by Category Chart
  const categoryOption = useMemo(() => {
    if (!reportData) return null;

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      grid: {
        top: 20,
        right: 20,
        bottom: 40,
        left: 50,
      },
      xAxis: {
        type: 'category',
        data: reportData.documentsByCategory.map(item => item.category),
      },
      yAxis: {
        type: 'value',
      },
      series: [
        {
          name: 'Documents',
          type: 'bar',
          data: reportData.documentsByCategory.map(item => item.count),
          itemStyle: { color: '#1E40AF' },
        },
      ],
    };
  }, [reportData]);

  // Verification Outcomes Chart (Donut)
  const outcomesOption = useMemo(() => {
    if (!reportData) return null;

    const data = Object.entries(reportData.verificationOutcomes).map(([name, value]) => ({
      name,
      value,
    }));

    const colors = {
      'Approved': '#10B981',
      'Rejected': '#EF4444',
      'Resubmission Required': '#F59E0B',
      'Expired': '#6B7280',
    };

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
      },
      legend: {
        orient: 'vertical',
        right: 10,
        top: 'center',
      },
      series: [
        {
          name: 'Outcomes',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 'bold',
            },
          },
          labelLine: {
            show: false,
          },
          data: data.map(item => ({
            ...item,
            itemStyle: { color: colors[item.name] || '#6B7280' },
          })),
        },
      ],
    };
  }, [reportData]);

  // Expiry Risk Window Chart (Stacked Bar)
  const expiryRiskOption = useMemo(() => {
    if (!reportData) return null;

    const data = Object.entries(reportData.expiryRiskWindow);

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      grid: {
        top: 20,
        right: 20,
        bottom: 40,
        left: 50,
      },
      xAxis: {
        type: 'value',
      },
      yAxis: {
        type: 'category',
        data: data.map(([key]) => key),
      },
      series: [
        {
          name: 'Documents',
          type: 'bar',
          data: data.map(([, value]) => value),
          itemStyle: {
            color: (params) => {
              const colors = ['#EF4444', '#F59E0B', '#FCD34D'];
              return colors[params.dataIndex];
            },
          },
        },
      ],
    };
  }, [reportData]);

  // Verification Method Chart
  const verificationMethodOption = useMemo(() => {
    if (!reportData) return null;

    const data = Object.entries(reportData.verificationMethod);

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      grid: {
        top: 20,
        right: 20,
        bottom: 40,
        left: 50,
      },
      xAxis: {
        type: 'category',
        data: data.map(([key]) => key),
      },
      yAxis: {
        type: 'value',
      },
      series: [
        {
          name: 'Documents',
          type: 'bar',
          data: data.map(([, value]) => value),
          itemStyle: { color: '#6366F1' },
        },
      ],
    };
  }, [reportData]);

  const handleExport = () => {
    console.log('Exporting aggregated data (CSV)...');
    // TODO: Implement CSV export with aggregated data only (no document IDs or business identifiers)
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading compliance reports...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        {/* Heading */}
        <AdminPageTitle>Compliance Reports</AdminPageTitle>

        {/* Search and Filters Row */}
        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            {/* Navigation Buttons - Left */}
            <div className="flex flex-wrap gap-3 w-full lg:w-auto">
              <AdminFormSecondaryAction type="button" onClick={() => navigate('/admin/verification/archive')}>
                View Compliance Archive
              </AdminFormSecondaryAction>
            </div>

            {/* Filters and Export - Right */}
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              {/* Compliance reports list filters */}
              <AdminListFilterDropdown
                label="Date Range"
                value={timeframe}
                options={TIMEFRAME_OPTIONS.map((option) => ({ value: option, label: option }))}
                onChange={setTimeframe}
                menuRef={timeframeRef}
                open={timeframeOpen}
                setOpen={setTimeframeOpen}
              />

              <AdminFormSecondaryAction
                type="button"
                onClick={() => setCompareMode(!compareMode)}
                className={
                  compareMode
                    ? '!bg-primaryblue !text-white !border-primaryblue hover:!bg-primaryblue/90'
                    : ''
                }
              >
                Compare to Previous
              </AdminFormSecondaryAction>

              <AdminHeaderAction onClick={handleExport}>
                Export CSV
              </AdminHeaderAction>
            </div>
          </div>
        </div>

        {/* Summary Indicators - ROW 1 */}
        {summaryIndicators && (
          <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 ${ADMIN_SECTION_OFFSET_CLASS}`}>
            <DashboardMainContainer className={`${ADMIN_METRIC_PANEL_CLASS} relative`}>
              <AdminSupportingText className="text-offblack mb-2">Documents Submitted</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{formatMetric(summaryIndicators.documentsSubmitted)}</AdminMetric>
              {renderDelta(8)}
            </DashboardMainContainer>

            <DashboardMainContainer className={`${ADMIN_METRIC_PANEL_CLASS} relative`}>
              <AdminSupportingText className="text-offblack mb-2">Pending Verification</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{formatMetric(summaryIndicators.pendingVerification)}</AdminMetric>
              {renderDelta(-12)}
            </DashboardMainContainer>

            <DashboardMainContainer className={`${ADMIN_METRIC_PANEL_CLASS} relative`}>
              <AdminSupportingText className="text-offblack mb-2">Avg Verification Time</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{summaryIndicators.avgVerificationTime}</AdminMetric>
              {renderDelta(-5)}
            </DashboardMainContainer>

            <DashboardMainContainer className={`${ADMIN_METRIC_PANEL_CLASS} relative`}>
              <AdminSupportingText className="text-offblack mb-2">Expiring in 30 Days</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{formatMetric(summaryIndicators.expiringIn30Days)}</AdminMetric>
              {renderDelta(15)}
            </DashboardMainContainer>

            <DashboardMainContainer className={`${ADMIN_METRIC_PANEL_CLASS} relative`}>
              <AdminSupportingText className="text-offblack mb-2">Non-Compliant Businesses</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{formatMetric(summaryIndicators.nonCompliantBusinesses)}</AdminMetric>
              {renderDelta(-8)}
            </DashboardMainContainer>
          </div>
        )}

        {/* Documents Submitted Over Time - ROW 2 */}
        <DashboardMainContainer className={`${ADMIN_CONTENT_PANEL_CLASS} ${ADMIN_SECTION_OFFSET_CLASS}`}>
          <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
            <AdminWidgetTitle className="text-offblack">
              Documents Submitted Over Time
            </AdminWidgetTitle>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSubmissionToggle('Submitted')}
                className={`px-3 py-1 rounded text-xs transition-colors ${
                  submissionToggle === 'Submitted'
                    ? 'bg-primaryblue text-white'
                    : 'bg-white text-offblack border border-lightgray hover:border-primaryblue'
                }`}
              >
                Submitted
              </button>
              <button
                onClick={() => setSubmissionToggle('Verified')}
                className={`px-3 py-1 rounded text-xs transition-colors ${
                  submissionToggle === 'Verified'
                    ? 'bg-primaryblue text-white'
                    : 'bg-white text-offblack border border-lightgray hover:border-primaryblue'
                }`}
              >
                Verified
              </button>
            </div>
          </div>
          <div className="flex-1 p-4">
            {documentsTrendOption && (
              <>
                <EChartComponent option={documentsTrendOption} style={{ width: '100%', height: '300px' }} />
                <AdminMetadataText className="text-mediumgray mt-4 text-center">
                  Includes licences, tickets, and badges.
                </AdminMetadataText>
              </>
            )}
          </div>
        </DashboardMainContainer>

        {/* Category Breakdowns - ROW 3 */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${ADMIN_SECTION_OFFSET_CLASS}`}>
          {/* Documents by Category */}
          <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
            <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
              <AdminWidgetTitle className="text-offblack">
                Documents by Category
              </AdminWidgetTitle>
            </div>
            <div className="flex-1 p-4">
              {categoryOption && (
                <EChartComponent option={categoryOption} style={{ width: '100%', height: '300px' }} />
              )}
            </div>
          </DashboardMainContainer>

          {/* Verification Outcomes */}
          <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
            <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
              <AdminWidgetTitle className="text-offblack">
                Verification Outcomes
              </AdminWidgetTitle>
            </div>
            <div className="flex-1 p-4">
              {outcomesOption && (
                <EChartComponent option={outcomesOption} style={{ width: '100%', height: '300px' }} />
              )}
            </div>
          </DashboardMainContainer>
        </div>

        {/* Risk & Process - ROW 4 */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${ADMIN_SECTION_OFFSET_CLASS}`}>
          {/* Expiry Risk Window */}
          <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
            <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
              <AdminWidgetTitle className="text-offblack">
                Expiry Risk Window
              </AdminWidgetTitle>
            </div>
            <div className="flex-1 p-4">
              {expiryRiskOption && (
                <EChartComponent option={expiryRiskOption} style={{ width: '100%', height: '300px' }} />
              )}
            </div>
          </DashboardMainContainer>

          {/* Verification Method */}
          <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
            <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
              <AdminWidgetTitle className="text-offblack">
                Verification Method
              </AdminWidgetTitle>
            </div>
            <div className="flex-1 p-4">
              {verificationMethodOption && (
                <EChartComponent option={verificationMethodOption} style={{ width: '100%', height: '300px' }} />
              )}
            </div>
          </DashboardMainContainer>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ComplianceReports;
